import React from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'

import { Common, useCommonStore } from '~/states/commonStore'

import theme from '~/styles/theme'

type WrapperProps = Pick<Common, 'mode'>

const Contents: React.FC<{ html: string }> = ({ html }) => {
  const {
    common: { mode },
  } = useCommonStore()

  return <ReactMarkDownWrapper dangerouslySetInnerHTML={{ __html: html }} mode={mode} />
}

export const BlockQuoteStyle = css`
  ${theme.typography.body1}
  margin: 30px 0;
  padding: 15px;

  p {
    margin-bottom: 0 !important;
  }

  @media ${theme.device.mobile} {
    margin: 20px 0;
    padding: 12px;
  }
`

const ReactMarkDownWrapper = styled.article<WrapperProps>`
  margin-bottom: 50px;

  @media ${({ theme: { device } }) => device.mobile} {
    margin-bottom: 30px;
  }

  * * {
    word-break: break-all;
    color: ${({ theme: { colors, palette }, mode }) =>
      mode === 'light' ? colors.deepText : palette.bodyGray};
  }

  h1,
  h2,
  h3,
  h4 {
    margin: 50px 0 25px;
    color: ${({ theme: { colors } }) => colors.deepText};
    font-weight: 900;

    @media ${({ theme: { device } }) => device.mobile} {
      margin: 40px 0 15px;
    }
  }

  h1 {
    font-weight: 700 !important;
  }

  h4 {
    font-size: 21px;
    color: ${({ theme: { colors } }) => colors.deepText};

    @media ${({ theme: { device } }) => device.mobile} {
      font-size: 17.5px;
    }
  }

  p,
  summary {
    ${({ theme: { typography } }) => typography.body1}
    margin-bottom: 20px;
    color: ${({ theme: { colors, palette }, mode }) =>
      mode === 'light' ? colors.deepText : palette.bodyGray};

    @media ${({ theme: { device } }) => device.mobile} {
      margin-bottom: 15px;
    }
  }

  details {
    margin-bottom: 0;

    ul {
      padding-left: 12px;
      padding-bottom: 10px;
    }
  }

  summary {
    cursor: pointer;

    :hover {
      text-decoration: underline;
    }
  }

  ul,
  ol {
    margin-left: 20px;
    padding-bottom: 25px;

    @media ${({ theme: { device } }) => device.mobile} {
      padding-bottom: 20px;
    }
  }

  ul li {
    list-style: square;
  }
  ol li {
    list-style: decimal !important;
  }

  li {
    ${({ theme: { typography } }) => typography.body1}
  }

  hr {
    margin: 40px 0;
    border: 1px solid ${({ theme: { colors } }) => colors.lightText};

    @media ${({ theme: { device } }) => device.mobile} {
      margin-bottom: 30px 0;
    }
  }

  a {
    color: ${({ theme: { colors } }) => colors.darkText};
    color: #999 !important;
    text-decoration: underline;
    font-style: italic;
  }

  code {
    ${({ theme: { typography } }) => typography.code}
    padding: 3.7px 5px 2px;
    background-color: ${({ mode, theme: { colors } }) =>
      mode === 'light' ? colors.lightText : colors.ghostText};
    border-radius: 3px;
    font-family: monospace;
    color: #eb5757;
  }

  deckgo-highlight-code {
    ${({ theme: { typography } }) => typography.code}
    border-radius: 10px;
    background-color: #292e33;
    word-break: break-all;
    margin-bottom: 20px;

    @media ${({ theme: { device } }) => device.mobile} {
      margin-bottom: 15px;
    }
  }

  blockquote {
    ${BlockQuoteStyle}
    border-left: 4px solid ${({ theme: { colors } }) => colors.deepText};
    background-color: ${({ theme: { colors } }) => colors.ghostText};
    color: ${({ theme: { colors } }) => colors.deepText};
  }

  pre[class*='language-'] {
    ${({ theme: { typography } }) => typography.body1}
    font-family: monospace;

    pre,
    .token {
      background: none !important;
    }

    code {
      all: inherit;
      padding: 0;
      margin: 0;
    }
  }

  .gatsby-resp-image-wrapper {
    max-width: 100% !important;

    .gatsby-resp-image-image,
    .gatsby-resp-image-background-image {
      border-radius: 10px;
    }
  }
`

export default Contents
